import {Box, ButtonBase, Tooltip} from '@mui/material';
import React from 'react';
import {useLocation} from 'react-router';
import RouterLink from '../../lib/RouterLink';
import type {NavItem} from './useNavItems';

type Props = {
  item: NavItem;
  /** Icon only (rail): the label becomes the name and the tooltip. */
  iconOnly?: boolean;
  /** Indented sub-item without an icon (forecast-map views). */
  nested?: boolean;
  onNavigate?: () => void;
};

/**
 * One shell destination: a real link (open in new tab works), marked
 * aria-current="page" when it is the current page. Selected = a 3px bar in
 * the brand fill (a sanctioned use of #33B5E5) + ink label at 600; the bar
 * and the weight carry the state, not the colour alone.
 */
export const NavLink = ({item, iconOnly, nested, onNavigate}: Props) => {
  const {pathname} = useLocation();
  const active = item.isActive(pathname);
  const external = item.to.startsWith('mailto:');
  const linkProps = external
    ? {component: 'a' as const, href: item.to}
    : {component: RouterLink, to: item.to};

  const link = (
    <ButtonBase
      {...linkProps}
      aria-current={active ? 'page' : undefined}
      aria-label={iconOnly ? item.label : undefined}
      aria-keyshortcuts={item.keyShortcuts?.aria}
      onClick={onNavigate}
      disableRipple
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: iconOnly ? 'center' : 'flex-start',
        gap: 1.25,
        width: '100%',
        minHeight: iconOnly ? 40 : 34,
        px: iconOnly ? 0 : nested ? 1.5 : 1.25,
        pl: nested ? 4.75 : undefined,
        borderRadius: 1,
        color: 'text.secondary',
        fontSize: '0.875rem',
        fontWeight: 500,
        textAlign: 'left',
        '& svg': {fontSize: 18, flexShrink: 0},
        '&:hover': {backgroundColor: 'action.hover'},
        '&[aria-current="page"]': {
          color: 'ink.main',
          fontWeight: 600,
          backgroundColor: 'action.selected',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: 2,
            backgroundColor: 'primary.main',
          },
        },
      }}>
      {!nested && item.icon}
      {!iconOnly && (
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {item.label}
        </Box>
      )}
    </ButtonBase>
  );

  // A shortcut is shown in the tooltip, not inline: beside the label it
  // truncated "Search for a location" in the 232px sidebar.
  // describeChild: the tooltip describes the link, it does not rename it.
  const shortcut = item.keyShortcuts?.label;
  return (
    <Box component="li" sx={{listStyle: 'none'}}>
      {iconOnly ? (
        <Tooltip
          title={shortcut ? `${item.label} (${shortcut})` : item.label}
          placement="right">
          {link}
        </Tooltip>
      ) : shortcut ? (
        <Tooltip title={shortcut} placement="right" describeChild>
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </Box>
  );
};

export const NavGroup = ({
  children,
  ...aria
}: {
  children: React.ReactNode;
  'aria-labelledby'?: string;
}) => (
  <Box
    component="ul"
    {...aria}
    sx={{m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.25}}>
    {children}
  </Box>
);
